import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { MonthlyAttendanceCalendar, type CalendarData } from '@/components/admin/reports/MonthlyAttendanceCalendar';
import YearlyAttendanceGrid from '@/components/admin/reports/YearlyAttendanceGrid';
import {
  teacherService,
  type TeacherAttendanceDetails,
  type TeacherAttendanceHistory,
} from '@/services/teacher';
import type { AttendanceStatus } from '@/types';
import type { TeacherDashboardResponse } from '@/types/teacher';
import { resolveUserPhoto } from '@/utils/avatars';

type HistoryViewMode = 'day' | 'month' | 'year';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function TeacherHistoryPage() {
  const now = new Date();
  const [viewMode, setViewMode] = useState<HistoryViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<string>(() => format(now, 'yyyy-MM-dd'));
  const [selectedMonth, setSelectedMonth] = useState<number>(() => now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(() => now.getFullYear());

  // Homeroom class info
  const { data: dashboard } = useQuery<TeacherDashboardResponse>({
    queryKey: ['teacher-dashboard'],
    queryFn: () => teacherService.getDashboard(),
  });

  // Recent history summary
  const { data: history, isLoading: isHistoryLoading } = useQuery<TeacherAttendanceHistory>({
    queryKey: ['teacher-history'],
    queryFn: () => teacherService.getHistory({}),
  });

  // Available years
  const { data: availableYears } = useQuery({
    queryKey: ['teacher-available-years'],
    queryFn: () => teacherService.getAvailableYears(),
  });

  // Day details for selectedDate
  const { data: details, isLoading: isDetailsLoading } = useQuery<TeacherAttendanceDetails>({
    queryKey: ['teacher-history-details', selectedDate],
    queryFn: () => teacherService.getAttendanceDetails(selectedDate),
    enabled: Boolean(selectedDate) && viewMode === 'day',
  });

  // Monthly overview calendar data
  const { data: monthlyData, isLoading: isMonthlyLoading } = useQuery<CalendarData[]>({
    queryKey: ['teacher-monthly-overview', selectedYear, selectedMonth],
    queryFn: () => teacherService.getMonthlyOverview({ year: selectedYear, month: selectedMonth }),
    enabled: viewMode === 'month',
  });

  // Yearly overview data
  const { data: yearlyData, isLoading: isYearlyLoading } = useQuery({
    queryKey: ['teacher-yearly-overview', selectedYear],
    queryFn: () => teacherService.getYearlyOverview({ year: selectedYear }),
    enabled: viewMode === 'year',
  });

  // Fallback initial selected date from history
  useEffect(() => {
    if (history?.summaries.length && !selectedDate) {
      setSelectedDate(history.summaries[0]?.date ?? format(new Date(), 'yyyy-MM-dd'));
    }
  }, [history, selectedDate]);

  const classDetails = Array.isArray(details) ? details : [];

  const aggregate = useMemo(() => {
    if (!classDetails.length) {
      return null;
    }

    return classDetails.reduce(
      (acc, klass) => {
        for (const submission of klass.submissions) {
          acc[submission.status] += 1;
        }
        acc.total += klass.submissions.length;
        return acc;
      },
      {
        present: 0,
        absent: 0,
        late: 0,
        leave: 0,
        total: 0,
      } as Record<AttendanceStatus | 'total', number>,
    );
  }, [classDetails]);

  const dayRate = useMemo(() => {
    if (!aggregate || aggregate.total === 0) return 0;
    return Math.round((aggregate.present / aggregate.total) * 1000) / 10;
  }, [aggregate]);

  const yearsRange = useMemo(() => {
    const min = availableYears?.min ?? (now.getFullYear() - 3);
    const max = availableYears?.max ?? (now.getFullYear() + 1);
    const years: number[] = [];
    for (let y = max; y >= min; y--) {
      years.push(y);
    }
    return years;
  }, [availableYears, now]);

  const handleDaySelectFromCalendar = (dateStr: string) => {
    setSelectedDate(dateStr);
    setViewMode('day');
  };

  const handleMonthSelectFromYear = (monthNum: number) => {
    setSelectedMonth(monthNum);
    setViewMode('month');
  };

  return (
    <DashboardShell title="Attendance History" role="teacher">
      <div className="space-y-6 pb-12">
        {/* ======================================================== */}
        {/* TOP CONTROL BAR: Mode Switcher (Day / Month / Year)      */}
        {/* ======================================================== */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">
                {dashboard?.className ?? 'Homeroom Class'}
              </span>
              <span className="text-[11px] font-bold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full border border-indigo-100">
                Attendance Archive
              </span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5">
              {viewMode === 'day' && `Day View · ${selectedDate ? format(parseISO(selectedDate), 'MMMM d, yyyy') : ''}`}
              {viewMode === 'month' && `Monthly Heatmap · ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`}
              {viewMode === 'year' && `Academic Year Grid · ${selectedYear}`}
            </h2>
          </div>

          {/* Period Mode Selector Chips (Day / Month / Year) */}
          <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200 gap-1 self-stretch sm:self-auto text-xs">
            <button
              type="button"
              data-mode="day"
              id="history-mode-day"
              onClick={() => setViewMode('day')}
              className={clsx(
                'flex-1 sm:flex-initial px-4 py-2 rounded-lg font-bold transition-all min-h-[38px] flex items-center justify-center gap-1.5',
                viewMode === 'day'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60',
              )}
            >
              <span>📅</span>
              <span>Day</span>
            </button>
            <button
              type="button"
              data-mode="month"
              id="history-mode-month"
              onClick={() => setViewMode('month')}
              className={clsx(
                'flex-1 sm:flex-initial px-4 py-2 rounded-lg font-bold transition-all min-h-[38px] flex items-center justify-center gap-1.5',
                viewMode === 'month'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60',
              )}
            >
              <span>🗓</span>
              <span>Month</span>
            </button>
            <button
              type="button"
              data-mode="year"
              id="history-mode-year"
              onClick={() => setViewMode('year')}
              className={clsx(
                'flex-1 sm:flex-initial px-4 py-2 rounded-lg font-bold transition-all min-h-[38px] flex items-center justify-center gap-1.5',
                viewMode === 'year'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60',
              )}
            >
              <span>📊</span>
              <span>Year</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 1. DAY VIEW                                              */}
        {/* ======================================================== */}
        {viewMode === 'day' && (
          <div className="space-y-6">
            {/* Date Picker Bar & Quick Navigation */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <label className="text-xs font-bold text-slate-700 shrink-0">Select Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setSelectedDate(format(new Date(), 'yyyy-MM-dd'))}
                  className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl border border-indigo-200 transition"
                >
                  Today
                </button>
              </div>

              {/* Recent Recorded Dates Pills */}
              {history?.summaries && history.summaries.length > 0 && (
                <div className="flex items-center gap-1.5 overflow-x-auto max-w-full pb-1 sm:pb-0 text-xs">
                  <span className="text-[11px] font-bold text-slate-400 uppercase mr-1">Recent:</span>
                  {history.summaries.slice(0, 5).map((s) => (
                    <button
                      key={s.date}
                      type="button"
                      onClick={() => setSelectedDate(s.date)}
                      className={clsx(
                        'px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all border',
                        s.date === selectedDate
                          ? 'bg-indigo-600 text-white font-bold border-indigo-600 shadow-xs'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200',
                      )}
                    >
                      {format(parseISO(s.date), 'MMM d')}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Day Stat Counters */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-xs flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-teal-800">Present</div>
                  <div className="text-2xl font-black text-teal-700">{aggregate?.present ?? 0}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-sm">✓</span>
              </div>
              <div className="bg-white p-4 rounded-2xl border border-rose-100 shadow-xs flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-rose-800">Absent</div>
                  <div className="text-2xl font-black text-rose-700">{aggregate?.absent ?? 0}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center font-bold text-sm">✕</span>
              </div>
              <div className="bg-white p-4 rounded-2xl border border-amber-100 shadow-xs flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-amber-800">Late</div>
                  <div className="text-2xl font-black text-amber-700">{aggregate?.late ?? 0}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center font-bold text-sm">⏱</span>
              </div>
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-700">Leave</div>
                  <div className="text-2xl font-black text-slate-800">{aggregate?.leave ?? 0}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm">📝</span>
              </div>
              <div className="bg-white p-4 rounded-2xl border border-indigo-100 shadow-xs col-span-2 sm:col-span-1 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase font-bold text-indigo-700">Attendance Rate</div>
                  <div className="text-2xl font-black text-indigo-900">{dayRate}%</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold text-sm">%</span>
              </div>
            </div>

            {/* Day Roster Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="p-4 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
                  <h3 className="font-bold text-sm text-slate-900">
                    Day Roster · {selectedDate ? format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy') : ''}
                  </h3>
                </div>
                {classDetails[0]?.editable !== undefined && (
                  <span className={clsx('text-xs font-bold px-2.5 py-1 rounded-full border', classDetails[0].editable ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-100 text-slate-600 border-slate-200')}>
                    {classDetails[0].editable ? 'Editable Window' : 'Locked Archive'}
                  </span>
                )}
              </div>

              {isDetailsLoading ? (
                <div className="p-12 text-center text-slate-400">Loading day attendance records…</div>
              ) : classDetails.length === 0 || !classDetails[0]?.submissions?.length ? (
                <div className="p-12 text-center text-slate-400 space-y-2">
                  <div className="text-2xl">📅</div>
                  <div className="font-semibold text-slate-600">No attendance submitted for this date</div>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Select another recorded date from the list above or switch to Month view to inspect the calendar heatmap.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left">Roll #</th>
                        <th className="px-4 py-3 text-left">Student Name</th>
                        <th className="px-4 py-3 text-right">Recorded Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {classDetails[0].submissions.map((sub) => (
                        <tr key={sub.studentId} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-4 py-4 font-bold font-mono text-sm sm:text-base text-slate-900">{sub.rollNumber}</td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3.5 min-w-0">
                              {sub.photo ? (
                                <div className="w-14 h-14 rounded-2xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100 ring-1 ring-slate-200 shadow-xs">
                                  <img
                                    src={resolveUserPhoto(sub.photo, 'student')}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <span className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-700 font-mono text-sm font-black flex items-center justify-center border border-indigo-100 shrink-0">
                                  {sub.rollNumber ? sub.rollNumber.slice(-2) : '—'}
                                </span>
                              )}
                              <span className="font-bold text-base lg:text-lg text-slate-900 truncate">{sub.studentName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span
                              className={clsx(
                                'inline-flex items-center px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-bold border',
                                sub.status === 'present' && 'bg-teal-50 text-teal-700 border-teal-200',
                                sub.status === 'absent' && 'bg-rose-50 text-rose-700 border-rose-200',
                                sub.status === 'late' && 'bg-amber-50 text-amber-700 border-amber-200',
                                sub.status === 'leave' && 'bg-slate-100 text-slate-700 border-slate-200',
                              )}
                            >
                              {sub.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* 2. MONTH VIEW (Monthly Attendance Calendar Heatmap)      */}
        {/* ======================================================== */}
        {viewMode === 'month' && (
          <div className="space-y-4">
            {/* Month / Year Filter Controls */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-700">Month:</span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {yearsRange.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const prevM = selectedMonth === 1 ? 12 : selectedMonth - 1;
                    const prevY = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
                    setSelectedMonth(prevM);
                    setSelectedYear(prevY);
                  }}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition"
                  title="Previous Month"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMonth(now.getMonth() + 1);
                    setSelectedYear(now.getFullYear());
                  }}
                  className="px-3 py-2 rounded-xl bg-indigo-50 text-indigo-700 font-bold text-xs border border-indigo-100 hover:bg-indigo-100 transition"
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const nextM = selectedMonth === 12 ? 1 : selectedMonth + 1;
                    const nextY = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
                    setSelectedMonth(nextM);
                    setSelectedYear(nextY);
                  }}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition"
                  title="Next Month"
                >
                  Next →
                </button>
              </div>
            </div>

            {/* Reusable Calendar Heatmap Grid Scoped to Class */}
            <MonthlyAttendanceCalendar
              data={monthlyData || []}
              year={selectedYear}
              month={selectedMonth}
              isLoading={isMonthlyLoading}
              highlightDate={selectedDate}
              onDayClick={handleDaySelectFromCalendar}
            />
          </div>
        )}

        {/* ======================================================== */}
        {/* 3. YEAR VIEW (Yearly Attendance Hotspot Grid V4-style)   */}
        {/* ======================================================== */}
        {viewMode === 'year' && (
          <div className="space-y-4">
            {/* Year Picker */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-700">Academic Year:</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm font-bold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {yearsRange.map((y) => (
                    <option key={y} value={y}>
                      {y} - {y + 1} ({y})
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-xs font-semibold text-slate-500">
                Click any month card to view its calendar heatmap
              </div>
            </div>

            {/* Reusable Yearly Hotspot Grid Scoped to Class */}
            <YearlyAttendanceGrid
              data={yearlyData?.months || []}
              isLoading={isYearlyLoading}
              onMonthClick={handleMonthSelectFromYear}
            />
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
